import * as React from 'react';
import './SingleSessionDetails.css';
import { Box, Divider, IconButton, Menu, MenuItem, Typography, Avatar } from '@mui/material';
import AdminNavbar from '../../../../../components/Admin/AdminNavbar/AdminNavbar';
import Layout from '../../../../../components/Layout';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import { axiosInstance } from '../../../../../utils/tokenUtils';
import { useState } from 'react';
import { useEffect } from 'react';
import { useCallback } from 'react';
import TeacherEnableModal from '../../../../../components/Admin/UserManagement/Teacher/TeacherEnableModal/TeacherEnableModal';
import CalenderIcon from '../../../../../assets/Admin/SessionIcons/calender-icon.svg';
import ClockIcon from '../../../../../assets/Admin/SessionIcons/clock-icon.svg';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CircularProgress from '@mui/material/CircularProgress';
import SessionRecordings from '../../../../../components/Admin/SessionRecordings/SessionRecordings';

// Initialize dayjs plugins for timezone conversion
dayjs.extend(utc);
dayjs.extend(timezone);

const StatusColor = {
  enable: { color: '#14B334', bgcolor: '#F2FFF5', value: 'Enabled' },
  disable: { color: '#949DA6', bgcolor: '#F5F5F5', value: 'Disabled' },
};

const SessionTypeColor = {
  one_on_one: { color: '#6B60E7', bgcolor: '#F0EFFF', value: 'One-On-One' },
  group: { color: '#14B334', bgcolor: '#F2FFF5', value: 'Group Sessions' },
  prime: { color: '#E0AD14', bgcolor: '#FFFBF0', value: 'Prime Sessions' },
};

// Helper function to convert UTC time to local time
const toLocalTime = utcTime => {
  if (!utcTime) return null;
  return dayjs.utc(utcTime).local();
};

// Helper function to format text (capitalize first letter and replace underscores with spaces)
const formatText = text => {
  if (!text) return '---';
  return text
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const SingleSessionDetails = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [rowId, setRowId] = useState(null);
  const [teacherName, setTeacherName] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [sessionDetails, setSessionDetails] = useState({});
  const [showAllStudents, setShowAllStudents] = useState(false);
  const [loading, setLoading] = useState(true);
  const [redirectToCorrectUrl, setRedirectToCorrectUrl] = useState(null);
  const navigate = useNavigate();
  const { teacherId, id } = useParams();

  const getSingleSessionDetails = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(`/admin/sessions/id/${id}`);
      setSessionDetails(data);

      // If teacherId is not in URL but we have it in the response, redirect to the correct URL
      if (!teacherId && data?.teacher?.id) {
        setRedirectToCorrectUrl(`/admin/users/instructor/${data.teacher.id}/session-details/${id}`);
      }

      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  }, [id, teacherId]);

  useEffect(() => {
    getSingleSessionDetails();
  }, [getSingleSessionDetails]);

  const handleMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = action => {
    if (action === 'disable') {
      setOpenApproveModal(true);
      setRowId(teacherId);
      setTeacherName(sessionDetails?.teacher?.name || '---');
      setIsActive(sessionDetails?.teacher?.is_active);
    } else if (action === 'enable') {
      setOpenApproveModal(true);
      setRowId(teacherId);
      setTeacherName(sessionDetails?.teacher?.name || '---');
      setIsActive(sessionDetails?.teacher?.is_active);
    }
    handleMenuClose();
  };

  const navigateToTeacherDetails = () => {
    if (teacherId) {
      navigate(`/admin/users/instructor-details/${teacherId}`);
    } else if (sessionDetails?.teacher?.id) {
      // Fallback to use the teacher ID from session details if URL param is missing
      navigate(`/admin/users/instructor-details/${sessionDetails.teacher.id}`);
    } else {
      // If no teacher ID is available, navigate to the instructor list
      navigate('/admin/users/instructor');
    }
  };

  const navigateToTeacherList = () => {
    navigate('/admin/users/instructor');
  };

  const formatTimeInterval = (startTime, lengthInMinutes) => {
    if (!startTime || !lengthInMinutes) return '---';

    const localStartTime = toLocalTime(startTime);
    const localEndTime = localStartTime.add(lengthInMinutes, 'minute');

    // Different scenarios
    const startHour = localStartTime.format('h:mm');
    const endHour = localEndTime.format('h:mm');
    const startPeriod = localStartTime.format('A');
    const endPeriod = localEndTime.format('A');

    // Handling AM/PM transition
    if (startPeriod !== endPeriod) {
      return `${startHour} ${startPeriod} - ${endHour} ${endPeriod}`;
    }

    // Same period (AM or PM)
    return `${startHour} - ${localEndTime.format('h:mm A')}`;
  };

  const formatDate = dateString => {
    if (!dateString) return '---';
    return toLocalTime(dateString).format('DD-MM-YYYY');
  };

  const formatDateLong = dateString => {
    if (!dateString) return '---';
    return toLocalTime(dateString).format('DD MMM, YYYY');
  };

  // If we need to redirect to the correct URL with teacherId
  if (redirectToCorrectUrl) {
    return <Navigate to={redirectToCorrectUrl} replace />;
  }

  if (loading) {
    return (
      <Layout title="Klass Online | Session Details">
        <Box className="flex justify-center items-center h-screen">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title="Klass Online | Session Details">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
          <Box className="flex flex-col p-[1rem] gap-3">
            <Box className="flex justify-between items-center">
              <Box className="flex flex-col">
                <Box className="flex items-center gap-2">
                  <Typography
                    className="user-management-request-text"
                    onClick={navigateToTeacherList}
                    style={{ cursor: 'pointer' }}
                  >
                    User Management {'>'}
                  </Typography>
                  <Typography
                    className="user-management-request-text"
                    onClick={navigateToTeacherList}
                    style={{ cursor: 'pointer' }}
                  >
                    {'>'} Instructor {'>'}
                  </Typography>
                  <Typography
                    className="user-management-request-text"
                    onClick={navigateToTeacherDetails}
                    style={{ cursor: 'pointer' }}
                  >
                    {'>'} Profile {'>'}
                  </Typography>
                  <Typography className="profile-text">Session</Typography>
                </Box>

                <Box className="flex items-center gap-4">
                  <Typography className="teacher-request-name-text">
                    {sessionDetails?.teacher?.name || '---'}
                  </Typography>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      height: '16px',
                      mt: 1,
                      borderColor: '#D5DEE3',
                    }}
                  />
                  <Box
                    sx={{
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '4px 8px 4px 8px',
                      borderRadius: '20px',
                      backgroundColor: sessionDetails?.teacher?.is_active
                        ? StatusColor['enable']?.bgcolor
                        : StatusColor['disable']?.bgcolor,
                      color: sessionDetails?.teacher?.is_active
                        ? StatusColor['enable']?.color
                        : StatusColor['disable']?.color,
                      border: `1px solid ${
                        sessionDetails?.teacher?.is_active
                          ? StatusColor['enable']?.color
                          : StatusColor['disable']?.color
                      }`,
                      fontFamily: 'Poppins !important',
                      fontSize: '10px !important',
                      fontStyle: 'normal !important',
                      fontWeight: '400 !important',
                      lineHeight: 'normal !important',
                    }}
                  >
                    <Typography
                      className="w-[6px] h-[6px] rounded-full"
                      style={{
                        backgroundColor: sessionDetails?.teacher?.is_active
                          ? StatusColor['enable']?.color
                          : StatusColor['disable']?.color,
                      }}
                    ></Typography>
                    <Typography className="status-text">
                      {sessionDetails?.teacher?.is_active === true ? 'Enabled' : 'Disabled'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box className="flex items-center gap-4">
                <IconButton onClick={e => handleMenuClick(e)}>
                  <MoreHorizOutlinedIcon />
                </IconButton>
              </Box>
            </Box>
            <Box className="session-details-container">
              {sessionDetails?.status === 'cancelled' && (
                <Box className="single-cancel-session-details-container">
                  <Box className="flex">
                    <Typography className="cancelled-on-text">Cancelled On</Typography>
                    <Typography className="cancelled-on-text">
                      {' '}
                      {formatDateLong(sessionDetails.updated_at)}
                    </Typography>
                  </Box>
                  <Box className="flex gap-4">
                    <Typography className="cancelled-by-text">Reason</Typography>
                    <Typography className="cancelled-by-value">
                      {sessionDetails.cancellation_reason || '---'}
                    </Typography>
                  </Box>
                  <Box className="flex gap-4">
                    <Typography className="cancelled-by-text">Cancelled By</Typography>
                    <Typography className="cancelled-by-value">
                      {sessionDetails.cancelled_by === 'teacher'
                        ? 'Instructor'
                        : sessionDetails.cancelled_by === 'student'
                          ? 'Learner'
                          : '---'}
                    </Typography>
                  </Box>
                </Box>
              )}
              <Box className="single-session-details-container">
                <Typography className="session-title-text">
                  {sessionDetails?.subject || '---'}
                </Typography>
                <Typography
                  className="session-type-text"
                  sx={{
                    color: SessionTypeColor[sessionDetails?.session_type]?.color,
                  }}
                >
                  {sessionDetails?.session_type
                    ? formatText(sessionDetails?.session_type) + ' Session'
                    : '---'}
                </Typography>
              </Box>
              <Box className="single-session-details-container">
                <Box className="flex gap-8">
                  <Box className="flex justify-center items-center gap-4">
                    <img src={CalenderIcon} alt="Calender" />
                    <Box className="flex flex-col">
                      <Typography className="date-heading-text">Date</Typography>
                      <Typography className="date-heading-value">
                        {formatDate(sessionDetails?.start_time)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box className="flex justify-center items-center gap-4">
                    <img src={ClockIcon} alt="Calender" />
                    <Box className="flex flex-col">
                      <Typography className="date-heading-text">Timing</Typography>
                      <Typography className="date-heading-value">
                        {formatTimeInterval(
                          sessionDetails?.start_time,
                          sessionDetails?.length_in_minutes
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box className="class-with-container">
                <Typography className="section-title">Class with</Typography>
                {sessionDetails?.students && sessionDetails.students.length > 0 ? (
                  <>
                    {sessionDetails.students
                      .slice(0, showAllStudents ? sessionDetails.students.length : 1)
                      .map((student, index) => (
                        <Box
                          key={student.id}
                          className="student-info"
                          sx={{
                            paddingBottom: '8px',
                            borderBottom:
                              index !== (showAllStudents ? sessionDetails.students.length - 1 : 0)
                                ? '1px solid #F0F0F0'
                                : 'none',
                          }}
                        >
                          <Avatar
                            src={student?.user?.profile_image || '/placeholder.jpg'}
                            alt={student?.user?.name}
                            sx={{
                              height: 40,
                              width: 40,
                              marginRight: 2,
                            }}
                          />
                          <Box className="student-details">
                            <Typography className="student-name">
                              {student?.user?.name || '---'}
                              {student?.user?.is_active && (
                                <span
                                  style={{ color: '#14B334', marginLeft: '8px', fontSize: '14px' }}
                                >
                                  •
                                </span>
                              )}
                            </Typography>
                            <Typography className="student-email">
                              {student?.user?.email || '---'}
                            </Typography>
                            {student?.school_name && student?.school_name !== 'Not Specified' && (
                              <Typography className="student-school">
                                {student?.school_name}
                              </Typography>
                            )}
                            {/* {student?.school_level && (
                              <Typography className="student-school-level">
                                {formatText(student?.school_level)}
                              </Typography>
                            )} */}
                          </Box>
                        </Box>
                      ))}
                    {sessionDetails.students.length > 1 && (
                      <Typography
                        onClick={() => setShowAllStudents(!showAllStudents)}
                        className="toggle-students"
                      >
                        {showAllStudents ? (
                          <>
                            Hide learners <KeyboardArrowUpIcon fontSize="small" />
                          </>
                        ) : (
                          <>
                            See all {sessionDetails.students.length} learners{' '}
                            <KeyboardArrowDownIcon fontSize="small" />
                          </>
                        )}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography sx={{ color: '#666' }}>No students assigned</Typography>
                )}
              </Box>

              {sessionDetails?.file_url && (
                <Box className="pdf-preview-container">
                  <PictureAsPdfIcon sx={{ color: '#FF0000' }} />

                  <a
                    href={`https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(
                      sessionDetails.file_url
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pdf-name"
                  >
                    Prepsheet.pdf
                  </a>
                </Box>
              )}

              {sessionDetails?.status === 'completed' && (
                <Box className="single-session-details-container">
                  <Typography className="section-title" sx={{ width: '100%', display: 'block' }}>
                    Session Recording
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
                    <SessionRecordings bookingId={sessionDetails?.id || id} />
                  </Box>
                </Box>
              )}

              {/* Conditional Rendering for Review or Koins Deducted */}
              {sessionDetails?.status === 'completed' &&
              (sessionDetails?.review ||
                (sessionDetails?.reviews && sessionDetails.reviews.length > 0)) ? (
                <Box className="review-container">
                  <Typography className="section-title" sx={{ width: '100%', display: 'block' }}>
                    Reviews
                  </Typography>
                  {(sessionDetails?.review
                    ? [sessionDetails.review]
                    : sessionDetails?.reviews || []
                  ).map((review, index) => (
                    <Box key={review?.id || index} className="review-header">
                      <Avatar
                        src={
                          review?.student?.profile_image ||
                          sessionDetails?.student?.profile_image ||
                          sessionDetails?.students?.[0]?.user?.profile_image ||
                          '/placeholder.jpg'
                        }
                        alt={
                          review?.student?.name ||
                          sessionDetails?.student?.name ||
                          sessionDetails?.students?.[0]?.user?.name ||
                          'Student'
                        }
                        sx={{
                          width: 40,
                          height: 40,
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          className="section-content"
                          sx={{
                            marginBottom: '8px !important',
                            padding: '0 !important',
                            lineHeight: '20px !important',
                          }}
                        >
                          {review?.feedback || review?.comment || 'No comment provided.'}
                        </Typography>
                        <Box
                          className="star-rating"
                          sx={{
                            marginTop: '0 !important',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '2px',
                          }}
                        >
                          {[...Array(5)].map((_, i) =>
                            i < (review?.rating || 0) ? (
                              <StarIcon key={i} sx={{ fontSize: '18px', color: '#FFB800' }} />
                            ) : (
                              <StarOutlineIcon
                                key={i}
                                sx={{ fontSize: '18px', color: '#D1D5DB' }}
                              />
                            )
                          )}
                        </Box>
                        {review?.teacher_response && (
                          <Box
                            sx={{
                              marginTop: '12px',
                              padding: '12px',
                              backgroundColor: '#F9FAFB',
                              borderRadius: '8px',
                              borderLeft: '3px solid #0A5EF3',
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: 'Poppins',
                                fontSize: '12px',
                                fontWeight: '600',
                                color: '#14171F',
                                marginBottom: '4px',
                              }}
                            >
                              Instructor Response:
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: 'Poppins',
                                fontSize: '14px',
                                color: '#56585E',
                                lineHeight: '20px',
                              }}
                            >
                              {review.teacher_response}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                sessionDetails?.status === 'cancelled' &&
                sessionDetails?.penalty_koins > 0 && (
                  <Box className="koins-deducted">
                    <Typography className="section-title">Koins - Deducted</Typography>
                    <Typography className="section-content">
                      Since the cancellation was done within 8 hours,{' '}
                      <div style={{ color: 'red' }}> {sessionDetails.penalty_koins} Koins</div>
                      has been deducted from your wallet as a penalties.
                    </Typography>
                  </Box>
                )
              )}
            </Box>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: { width: '150px' },
            }}
          >
            <MenuItem
              onClick={() =>
                handleAction(sessionDetails?.teacher?.is_active === true ? 'disable' : 'enable')
              }
            >
              {sessionDetails?.teacher?.is_active === true ? 'Disable' : 'Enable'}
            </MenuItem>
          </Menu>
        </Box>
        {openApproveModal && (
          <TeacherEnableModal
            setOpenApproveModal={setOpenApproveModal}
            rowId={rowId}
            teacherName={teacherName}
            isActive={isActive}
          />
        )}
      </Box>
    </Layout>
  );
};

export default SingleSessionDetails;
